import React, { useEffect, useState } from "react";
import { Calendar, PlusCircle, Trash2, TrendingUp } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export default function MatchLogs({ onOpenLogModal, refreshKey }) {
  const [matches, setMatches] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [matchRes, summaryRes] = await Promise.all([
        api.get("/matches"),
        api.get("/analytics/summary"),
      ]);
      setMatches(matchRes.data);
      setSummary(summaryRes.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [refreshKey]);

  const remove = async (id) => {
    if (!window.confirm("Delete this match?")) return;
    try {
      await api.delete(`/matches/${id}`);
      toast.success("Match removed");
      setMatches((prev) => prev.filter((m) => m.id !== id));
      // Refresh summary after deletion
      const { data } = await api.get("/analytics/summary");
      setSummary(data);
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 bg-[#141414] border border-[#262626] p-6 rounded-xl">
        <div>
          <h3 className="text-2xl font-black uppercase text-white">Match Logs</h3>
          <p className="text-xs text-[#A3A3A3]">Every innings recorded with batting & bowling breakdown</p>
        </div>
        <button
          data-testid="matches-add-btn"
          onClick={onOpenLogModal}
          className="bg-[#FF3B30] hover:bg-[#E03229] text-white px-5 py-2.5 rounded text-sm font-bold flex items-center gap-2 shadow-lg"
        >
          <PlusCircle className="w-4 h-4" /> Log New Match
        </button>
      </div>

      {summary && summary.total_matches > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <SummaryCard label="Matches" value={summary.total_matches} />
          <SummaryCard label="Total Runs" value={summary.total_runs} />
          <SummaryCard label="Bat Avg" value={summary.batting_average} />
          <SummaryCard label="Wickets" value={summary.total_wickets} />
          <SummaryCard label="Economy" value={summary.bowling_economy} />
        </div>
      )}


      {loading && <div className="text-center text-sm text-[#A3A3A3] py-8 font-mono">Loading matches...</div>}
      {!loading && matches.length === 0 && (
        <div className="text-center text-sm text-[#A3A3A3] bg-[#141414] border border-dashed border-[#262626] rounded-xl p-10">
          No matches logged yet. Click "Log New Match" to record your first innings.
        </div>
      )}

      <div className="space-y-4" data-testid="match-list">
        {matches.map((m) => (
          <div key={m.id} data-testid={`match-card-${m.id}`} className="bg-[#141414] border border-[#262626] rounded-xl p-6 hover:border-[#FF3B30]/40 transition-all space-y-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 border-b border-[#262626] pb-4">
              <div className="flex items-center gap-3">
                <div className="bg-[#262626] text-white font-mono text-xs px-2.5 py-1 rounded font-bold">{m.format}</div>
                <div>
                  <h4 className="text-lg font-bold text-white">vs {m.opponent}</h4>
                  <p className="text-xs text-[#A3A3A3] flex items-center gap-1.5 mt-0.5">
                    <Calendar className="w-3.5 h-3.5 text-[#FF3B30]" /> {m.date} • {m.venue}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-right">
                  <span className="text-xs text-[#A3A3A3] block font-mono">Batting</span>
                  <span className="text-xl font-black text-white">
                    {m.batting.runs} <span className="text-xs text-[#A3A3A3]">({m.batting.balls}b)</span>
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-xs text-[#A3A3A3] block font-mono">Bowling</span>
                  <span className="text-xl font-black text-[#007AFF]">
                    {m.bowling.wickets}/{m.bowling.runs_conceded} <span className="text-xs text-[#A3A3A3]">({m.bowling.overs}o)</span>
                  </span>
                </div>
                <button
                  data-testid={`match-delete-${m.id}`}
                  onClick={() => remove(m.id)}
                  className="p-2 rounded bg-[#1A1A1A] border border-[#262626] text-[#A3A3A3] hover:text-[#FF3B30] hover:border-[#FF3B30]/50"
                  title="Delete match"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-1 text-xs">
              <MiniStat label="Boundaries" value={`${m.batting.fours} 4s • ${m.batting.sixes} 6s`} />
              <MiniStat label="Dismissal" value={m.batting.dismissal_type} color="text-[#FF3B30]" />
              <MiniStat label="vs Bowler" value={m.batting.bowler_type || "—"} />
              <MiniStat label="Dot Balls" value={`${m.bowling.dot_balls} dots`} color="text-emerald-400" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MiniStat({ label, value, color = "text-white" }) {
  return (
    <div className="bg-[#1A1A1A] p-3 rounded-lg border border-[#262626]">
      <span className="text-[#A3A3A3] block">{label}</span>
      <span className={`${color} font-bold text-sm`}>{value}</span>
    </div>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div className="bg-[#141414] border border-[#262626] rounded-lg p-4 text-center">
      <p className="text-[#A3A3A3] text-xs font-mono uppercase">{label}</p>
      <p className="text-white text-xl font-black mt-1">{value}</p>
    </div>
  );
}
